/**
 * Sun tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_subjects`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const sunTattoosPack: ToilePack = {
    slug: 'sun-tattoos',
    category: 'tattoo',
    name: 'Sun tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'face', label: 'Sun with face', subject: 'a round sun with a calm human face, wavy and straight rays alternating around it'},
        {key: 'geometric', label: 'Geometric sun', subject: 'a small circle inside a ring of fine straight rays of two lengths, a few dots between the rays'},
        {key: 'sunmoon', label: 'Sun and moon', subject: "a sun and a crescent moon joined into one circle, the sun's rays on one half and the moon's curve on the other"},
    ],
    styles: [
        {key: 'traditional', label: 'Traditional', style: 'American traditional tattoo flash, bold black outlines, heavy black shading and a limited flat palette', palette: 'red, yellow and green accents with black', framing: 'centered with a wide white margin'},
        {key: 'fineline', label: 'Fine line', style: 'a single-needle fine line tattoo drawing, one uniform hairline stroke, delicate detail and almost no shading', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, a thin outline with all shading built from stippled dots and dot gradients', palette: 'black ink only', framing: 'centered with a wide white margin, the dots reading cleanly'},
    ],
    directions: ['a star', 'orange ink', 'ankle'],
    directionHint: 'try: a star',
    seo: {
        title: 'Sun tattoo designs: face, geometric sun, sun and moon',
        metaDescription: 'Nine sun tattoo designs: a sun with a face, a geometric sun and a sun and crescent moon in one circle, in three styles.',
        h1: 'Sun tattoo designs: sun with a face, geometric sun, sun and moon',
        tagline: 'Three suns, each drawn in traditional flash, fine line and dotwork on white paper.',
        intro: 'Sun tattoo designs, worn for warmth, life and new days, from a small sign on the ankle to a large piece on the back. The nine sheets show a round sun with a calm face, a fine geometric sun with straight rays, and a sun and crescent moon joined in one circle. Each is drawn as traditional flash, fine line and dotwork. Type a different star or twist, such as orange ink, and the same styles can be asked for.',
        useCases: [
            'a small sun on the ankle',
            'a back piece with a sun and moon',
            'a geometric sun for the forearm',
            'a printed sheet to compare styles',
        ],
        faq: [
            {q: 'What is on the page?', a: 'Nine sun designs: a sun with a face, a geometric sun and a sun and moon, each in three styles.'},
            {q: 'Is this a design on skin?', a: 'No. Each design is a drawing on white paper, for taking to a studio or printing.'},
            {q: 'Can I ask for a star or a different colour?', a: 'Yes. Type a different star or twist, such as orange ink; the result is a new drawing, not a guaranteed match.'},
            {q: 'What is the difference between the styles?', a: 'Traditional flash has bold outlines and a few accent colours. Fine line is one uniform hairline with little shading. Dotwork builds the shadows from dots.'},
        ],
    },
}
